import { fetchEmailCampaignStatsThunk, resetEmailCampaign } from '@spike/email-marketing-action';
import { EmailCampaignStats } from '@spike/email-marketing-model';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { MarketingEmailsPath } from 'routes/Paths';
import { RootState } from 'store';

import DetailHeader from './UI/DetailHeader';
import InfoPanel from './UI/InfoPanel';
import MetricsRow from './UI/MetricsRow';
import ViewEmailModal from './UI/ViewEmailModal';

import styles from './EmailCampaignDetail.module.scss';

interface RouteParams {
    id?: string;
}

/**
 * Read-only detail view for a sent email campaign. Renders header + info
 * panel + metric cards + (optionally overlayed) the ViewEmail full-window
 * modal.
 *
 * Data flow:
 *   - Reads `:id` from the URL, dispatches `fetchEmailCampaignStatsThunk` on
 *     mount, and drives its render off the `stats` slice populated by the
 *     lib.
 *   - Unmount cleanup dispatches `resetEmailCampaign`, which the reducer
 *     interprets as "clear currentCampaign + stats". Prevents the next open
 *     of any detail view from flashing the previous campaign's data.
 *
 * Render states:
 *   - Loading (initial fetch in flight, no data yet) → Spinner.
 *   - Error / not-found (fetch resolved, no data) → friendly empty card with
 *     a back button. Covers 404 (invalid id), 422 (deleted while viewing),
 *     and network failures alike — we don't need to disambiguate for the
 *     user in MVP.
 *   - Loaded → header + info + metrics + iframe modal (gated by local state).
 */
const EmailCampaignDetail: FunctionComponent = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const apiClientWrapper = useApiClientWrapper();
    const { id: idParam } = useParams<RouteParams>();
    const campaignId = idParam ? Number(idParam) : NaN;

    const stats = useSelector<RootState, EmailCampaignStats | null>(state => state.emailCampaigns.stats.data);
    const loading = useSelector<RootState, boolean>(state => state.emailCampaigns.stats.loading);
    const error = useSelector<RootState, boolean>(state => state.emailCampaigns.stats.error);

    const [viewEmailOpen, setViewEmailOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!Number.isFinite(campaignId)) return;
        dispatch(fetchEmailCampaignStatsThunk(apiClientWrapper, campaignId));
        return () => {
            dispatch(resetEmailCampaign());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    const handleBack = () => {
        // `push` (not `goBack`) keeps the flow deterministic — a user landing
        // here from a deep link (bookmark, share) still gets a functional back
        // action instead of trying to pop history that isn't there.
        history.push(MarketingEmailsPath);
    };

    const handleOpenViewEmail = () => setViewEmailOpen(true);
    const handleCloseViewEmail = () => setViewEmailOpen(false);

    // -----------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------

    if (!Number.isFinite(campaignId)) {
        return (
            <div className={styles.container}>
                <div className={styles.errorCard}>
                    <h2 className={styles.errorTitle}>Invalid campaign id</h2>
                    <p className={styles.errorBody}>The URL for this campaign isn&apos;t valid.</p>
                    <Button
                        label="Back to campaigns"
                        onClick={handleBack}
                    />
                </div>
            </div>
        );
    }

    if (loading || (!stats && !error)) {
        return (
            <div className={styles.container}>
                <div className={styles.loaderContainer}>
                    <Spinner />
                </div>
            </div>
        );
    }

    if (error || !stats) {
        return (
            <div className={styles.container}>
                <div className={styles.errorCard}>
                    <h2 className={styles.errorTitle}>Campaign not found</h2>
                    <p className={styles.errorBody}>
                        We couldn&apos;t load this campaign. It may have been deleted or you don&apos;t have access.
                    </p>
                    <Button
                        label="Back to campaigns"
                        onClick={handleBack}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <DetailHeader
                campaign={stats.campaign}
                sentAt={stats.sentAt}
                onBack={handleBack}
                onViewEmail={handleOpenViewEmail}
            />

            <InfoPanel
                campaign={stats.campaign}
                recipientsCount={stats.recipientsCount}
                sentAt={stats.sentAt}
            />

            <MetricsRow
                openRate={stats.openRate}
                clickRate={stats.clickRate}
                sent={stats.sent}
            />

            {viewEmailOpen && (
                <ViewEmailModal
                    stats={stats}
                    onClose={handleCloseViewEmail}
                />
            )}
        </div>
    );
};

export default EmailCampaignDetail;
